import React, {ReactNode, useEffect, useRef} from 'react'
import {Box, useMultiStyleConfig, useMediaQuery} from '@chakra-ui/react'
import {Swiper, SwiperSlide} from 'swiper/react'
import {Pagination, Mousewheel, Keyboard, EffectCreative} from 'swiper/modules'
import {useHeaderSize, useViewport, useAnalytics} from '_customHooks'
// import {useHeaderDispatch} from '_context'

interface AnimatedSlideProps {
    data: {
        title_animation_type: 'no-animation' | 'fixed' | 'size' | 'attached' | 'fade' | 'carousel'
        module_attached?: boolean
        svg?: {url: string; metadata: {height: number; width: number}}
        title?: string
        cta_text?: string
        cta_url?: string
        cta2_text?: string
        cta2_url?: string
        cta_inline_d?: boolean
        cta_inline_m?: boolean
        content_color?: string
        content_align?: 'left' | 'center' | 'right'
        class_name?: string
        custom_css?: string
    }
    moduleItems: Array<ReactNode>
}

/**
 * The page can be in one of three scroll ownership states:
 * - swiper: the vertical carousel owns the scroll and the document body is locked.
 * - releasing-footer: the carousel has just released the document scroll, but the first programmatic scroll is still settling.
 * - footer: the regular document/footer owns the scroll and Swiper mousewheel handling is disabled.
 */
type ScrollMode = 'swiper' | 'releasing-footer' | 'footer'

/** Touch threshold used to detect a deliberate vertical swipe on touch devices. */
const SWIPE_THRESHOLD = 50

/**
 * Small safety delay after reaching the last slide before allowing the footer release.
 * This prevents the same strong gesture that changed slide from immediately releasing the footer.
 */
const LAST_SLIDE_RELEASE_DELAY = 800

/** Accumulated wheel delta required to release the footer from the last slide. */
const WHEEL_FOOTER_RELEASE_THRESHOLD = 90

/** Time window used to reset the wheel release intent if wheel events stop arriving. */
const WHEEL_RELEASE_RESET_DELAY = 180

/**
 * Delay used before starting the first smooth scroll into the footer.
 * This avoids competing in the same paint frame with Swiper creative transforms and body overflow changes.
 */
const FOOTER_SCROLL_DELAY = 180

/** How much of the viewport should be scrolled when entering the footer for the first time. */
const FOOTER_INITIAL_SCROLL_RATIO = 0.8

interface AnimatedSlideSwiper {
    slides: ArrayLike<unknown>
    realIndex: number
    slideNext: () => void
    slidePrev: (...args: Array<unknown>) => unknown
    animating?: boolean
    mousewheel?: {
        enable: () => void
        disable: () => void
    }
    pagination?: {
        el?: HTMLElement
    }
}

const DESKTOP_OVERLAP_EFFECT = {
    limitProgress: 2,
    prev: {
        translate: [0, '-24%', 0],
        opacity: 0.92,
    },
    next: {
        translate: [0, '100%', 0],
    },
}

const AnimatedSlide: React.FC<AnimatedSlideProps> = ({data, moduleItems}) => {
    const {title_animation_type} = data
    const {isDesktop, breakpoint} = useViewport()
    const {headerSize, accessibilityBannerSize} = useHeaderSize(true)
    const {headerSize: headerSizeNotCollapsed} = useHeaderSize(false)
    const [isWide] = useMediaQuery('(min-aspect-ratio: 20/11.25)')
    const [isUltrawide] = useMediaQuery('(min-aspect-ratio: 21/10)')
    const {analyticsEventCustom} = useAnalytics()

    const useDesktopOverlapEffect = moduleItems.length > 1
    const containerRef = useRef<HTMLDivElement>(null)
    const prevSlideIndex = useRef<number>(0)
    const swiperRef = useRef<AnimatedSlideSwiper | null>(null)

    /** Single source of truth for scroll ownership. */
    const scrollModeRef = useRef<ScrollMode>('swiper')

    /** Timestamp used to tell whether the user is intentionally trying to leave the last slide. */
    const lastSlideEnteredAtRef = useRef(0)

    /** Accumulator for wheel-based release intent while the user is on the last slide. */
    const wheelFooterDeltaRef = useRef(0)
    const wheelReleaseResetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const footerScrollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    /** Touch gesture refs. These avoid re-rendering while we track gesture state. */
    const touchStartYRef = useRef(0)
    const touchStartXRef = useRef(0)
    const touchEdgeStartYRef = useRef<number | null>(null)
    const touchTriggeredRef = useRef(false)

    const styles = useMultiStyleConfig('AnimatedSlide', {
        headerSize,
        headerSizeNotCollapsed,
        accessibilityBannerSize,
        isWide,
        isUltrawide,
    }) as {container: Record<string, unknown>; moduleItemWrapper: Record<string, unknown>}

    const getLastSlideIndex = (swiper: AnimatedSlideSwiper) => swiper.slides.length - 1
    const isLastSlide = (swiper: AnimatedSlideSwiper) => swiper.realIndex === getLastSlideIndex(swiper)
    const isFirstSlide = (swiper: AnimatedSlideSwiper) => swiper.realIndex === 0
    const isSwiperMode = () => scrollModeRef.current === 'swiper'
    const isFooterMode = () => scrollModeRef.current === 'footer'
    const isReleasingFooterMode = () => scrollModeRef.current === 'releasing-footer'

    const clearFooterScrollTimer = () => {
        if (footerScrollTimerRef.current) {
            clearTimeout(footerScrollTimerRef.current)
            footerScrollTimerRef.current = null
        }
    }

    const handlePaginationClick = (event: Event) => {
        const bullet = (event.target as HTMLElement).closest('.swiper-pagination-bullet')
        if (!bullet) return

        analyticsEventCustom({
            category: 'scroll_block',
            action: 'click',
            label: 'click',
        })
    }

    const resetFooterWheelIntent = () => {
        wheelFooterDeltaRef.current = 0

        if (wheelReleaseResetTimerRef.current) {
            clearTimeout(wheelReleaseResetTimerRef.current)
            wheelReleaseResetTimerRef.current = null
        }
    }

    /**
     * Centralized mode setter.
     * Keeping body overflow and Swiper mousewheel state here avoids scattered side effects.
     */
    const setScrollMode = (mode: ScrollMode) => {
        scrollModeRef.current = mode

        if (mode === 'swiper') {
            clearFooterScrollTimer()
            resetFooterWheelIntent()
            swiperRef.current?.mousewheel?.enable()
            document.body.style.overflow = 'hidden'
            return
        }

        swiperRef.current?.mousewheel?.disable()
        document.body.style.overflow = ''
    }

    const lockPageScroll = () => {
        setScrollMode('swiper')
    }

    const getActiveSlideElement = () => {
        return containerRef.current?.querySelector(
            ':scope > .swiper > .swiper-wrapper > .swiper-slide-active',
        ) as HTMLElement | null
    }

    const isScrollableElementAtBottom = (element: HTMLElement) => {
        return Math.ceil(element.scrollTop) + element.clientHeight >= element.scrollHeight - 1
    }

    const isScrollableElementAtTop = (element: HTMLElement) => {
        return element.scrollTop <= 0
    }

    const isElementScrollable = (element: HTMLElement) => {
        return element.scrollHeight > element.clientHeight + 1
    }

    /**
     * Nested horizontal Swipers must keep ownership of horizontal gestures.
     * This prevents the vertical carousel from stealing horizontal swipe/wheel interactions.
     */
    const isHorizontalNestedSwiperInteraction = (eventTarget: EventTarget | null, deltaX = 0, deltaY = 0) => {
        if (!(eventTarget instanceof HTMLElement)) return false

        const nestedHorizontalSwiper = eventTarget.closest('.swiper-horizontal')
        if (!nestedHorizontalSwiper) return false

        return Math.abs(deltaX) > Math.abs(deltaY)
    }

    /**
     * The carousel can be left only when the current slide is the last one and the release delay has elapsed.
     * This makes the footer release an explicit second intent, not a side effect of reaching the last slide.
     */
    const canLeaveCarousel = (swiper: AnimatedSlideSwiper) => {
        return isLastSlide(swiper) && Date.now() - lastSlideEnteredAtRef.current >= LAST_SLIDE_RELEASE_DELAY
    }

    /**
     * Shared check used by both wheel and touch flows.
     * The footer can be released only if the active slide cannot consume more downward scroll.
     */
    const canActiveSlideReleaseFooter = (swiper: AnimatedSlideSwiper, activeSlide: HTMLElement) => {
        if (!canLeaveCarousel(swiper)) return false

        const slideScrollable = isElementScrollable(activeSlide)
        return !slideScrollable || isScrollableElementAtBottom(activeSlide)
    }

    /**
     * Releases the page from carousel mode into footer mode.
     * Swiper mousewheel is disabled so wheel events over the carousel area are passed to the page.
     * The smooth scroll is delayed slightly to avoid a visual bounce caused by changing overflow and animating in the same frame.
     */
    const releasePageScrollToFooter = () => {
        if (!isSwiperMode()) return

        setScrollMode('releasing-footer')

        requestAnimationFrame(() => {
            clearFooterScrollTimer()

            footerScrollTimerRef.current = setTimeout(() => {
                window.scrollBy({
                    top: window.innerHeight * FOOTER_INITIAL_SCROLL_RATIO,
                    behavior: 'smooth',
                })
            }, FOOTER_SCROLL_DELAY)
        })
    }

    /**
     * Accumulates wheel energy while on the last slide.
     * This avoids releasing the footer on tiny accidental wheel deltas, but keeps the behavior trackpad-friendly.
     */
    const tryReleaseFooterFromWheel = (deltaY: number, swiper: AnimatedSlideSwiper, activeSlide: HTMLElement) => {
        if (!canActiveSlideReleaseFooter(swiper, activeSlide)) {
            resetFooterWheelIntent()
            return false
        }

        wheelFooterDeltaRef.current += deltaY

        if (wheelReleaseResetTimerRef.current) {
            clearTimeout(wheelReleaseResetTimerRef.current)
        }

        wheelReleaseResetTimerRef.current = setTimeout(() => {
            resetFooterWheelIntent()
        }, WHEEL_RELEASE_RESET_DELAY)

        if (wheelFooterDeltaRef.current >= WHEEL_FOOTER_RELEASE_THRESHOLD) {
            releasePageScrollToFooter()
            return true
        }

        return false
    }

    /**
     * Slide-change side effects are kept small:
     * - Entering the last slide records the timestamp used by footer release logic.
     * - Leaving the last slide restores carousel-owned scroll.
     * - The window is kept aligned with the carousel area while navigating slides.
     */
    const handleSlideChange = (swiper: AnimatedSlideSwiper): void => {
        if (isLastSlide(swiper)) {
            lastSlideEnteredAtRef.current = Date.now()
            resetFooterWheelIntent()
        } else {
            lockPageScroll()
        }

        if (!isFirstSlide(swiper)) {
            window.scrollTo({top: 1, behavior: 'instant'})
        }

        if (isFirstSlide(swiper)) {
            window.scrollTo({top: 0, behavior: 'instant'})
        }

        if (!isFirstSlide(swiper)) {
            containerRef.current?.classList.add('not-on-first-slide')
        } else {
            containerRef.current?.classList.remove('not-on-first-slide')
        }

        prevSlideIndex.current = swiper.realIndex
    }

    const activateReverseTransition = () => {
        containerRef.current?.classList.add('is-reverse-transition')
    }

    const handleSlideChangeTranstionEnd = (): void => {
        containerRef.current?.classList.remove('is-reverse-transition')
    }

    const calculateSpaceBetweenSlides = () => {
        if (!breakpoint) {
            return 0
        }

        if (!['xl', '2xl'].includes(breakpoint)) {
            return 0
        }

        return (headerSize[breakpoint] - (accessibilityBannerSize[breakpoint] * 2 || 0)) * -1
    }

    /**
     * Touch fallback for devices where the Swiper mousewheel module is not involved.
     * It allows native scrolling inside a slide, changes slide only at vertical scroll boundaries,
     * and releases the footer only after an extra downward intent on the last slide.
     */
    useEffect(() => {
        const container = containerRef.current
        if (!container) return

        const handleTouchStart = (event: TouchEvent) => {
            touchStartYRef.current = event.touches[0].clientY
            touchStartXRef.current = event.touches[0].clientX
            touchEdgeStartYRef.current = null
            touchTriggeredRef.current = false
        }

        const handleTouchMove = (event: TouchEvent) => {
            const swiper = swiperRef.current
            if (!swiper || swiper.animating || touchTriggeredRef.current || !isSwiperMode()) return

            const currentY = event.touches[0].clientY
            const currentX = event.touches[0].clientX
            const deltaY = touchStartYRef.current - currentY
            const deltaX = touchStartXRef.current - currentX

            if (isHorizontalNestedSwiperInteraction(event.target, deltaX, deltaY)) {
                return
            }

            const activeSlide = getActiveSlideElement()
            if (!activeSlide) return

            const scrollingDown = deltaY > 0
            const scrollingUp = deltaY < 0
            const slideScrollable = isElementScrollable(activeSlide)
            const activeSlideAtBottom = !slideScrollable || isScrollableElementAtBottom(activeSlide)
            const activeSlideAtTop = !slideScrollable || isScrollableElementAtTop(activeSlide)

            if (isLastSlide(swiper) && scrollingDown && activeSlideAtBottom) {
                if (!canLeaveCarousel(swiper)) {
                    event.preventDefault()
                    return
                }

                if (touchEdgeStartYRef.current === null) {
                    touchEdgeStartYRef.current = currentY
                }

                const edgeDelta = touchEdgeStartYRef.current - currentY

                if (edgeDelta > SWIPE_THRESHOLD) {
                    touchTriggeredRef.current = true
                    event.preventDefault()
                    releasePageScrollToFooter()
                }

                return
            }

            if (slideScrollable && !activeSlideAtBottom && scrollingDown) {
                return
            }

            if (slideScrollable && !activeSlideAtTop && scrollingUp) {
                return
            }

            if (Math.abs(deltaY) <= SWIPE_THRESHOLD) {
                return
            }

            touchTriggeredRef.current = true
            event.preventDefault()

            if (scrollingDown && !isLastSlide(swiper)) {
                swiper.slideNext()
            } else if (scrollingUp && !isFirstSlide(swiper)) {
                swiper.slidePrev()
            }
        }

        container.addEventListener('touchstart', handleTouchStart, {passive: true})
        container.addEventListener('touchmove', handleTouchMove, {passive: false})

        return () => {
            container.removeEventListener('touchstart', handleTouchStart)
            container.removeEventListener('touchmove', handleTouchMove)
        }
    }, [])

    /**
     * Initial page setup.
     * The document starts locked so the first scroll gestures are owned by the vertical carousel.
     */
    useEffect(() => {
        window.scrollTo({top: 0, behavior: 'instant'})
        lockPageScroll()

        requestAnimationFrame(() => {
            window.scrollTo({top: 0, behavior: 'instant'})
        })

        return () => {
            document.body.style.overflow = ''
            clearFooterScrollTimer()
            resetFooterWheelIntent()
            scrollModeRef.current = 'swiper'
        }
    }, [])

    /**
     * Re-entry from footer to carousel.
     * Once the footer owns the scroll, the page scrolls normally.
     * If the user scrolls back to the top, the carousel takes ownership again and stays on the last slide.
     */
    useEffect(() => {
        const handleWindowScroll = () => {
            const swiper = swiperRef.current
            if (!swiper || isSwiperMode() || !isLastSlide(swiper)) return

            if (isReleasingFooterMode()) {
                if (window.scrollY > 2) {
                    scrollModeRef.current = 'footer'
                }

                return
            }

            if (isFooterMode() && window.scrollY <= 1) {
                lockPageScroll()

                requestAnimationFrame(() => {
                    window.scrollTo({top: 1, behavior: 'instant'})
                })
            }
        }

        window.addEventListener('scroll', handleWindowScroll, {passive: true})

        return () => {
            window.removeEventListener('scroll', handleWindowScroll)
        }
    }, [])

    /**
     * Wheel release bridge for the last slide.
     * Swiper still handles normal wheel navigation between slides.
     * This listener only intercepts downward wheel events on the last slide after the active slide reached its bottom.
     */
    useEffect(() => {
        const container = containerRef.current
        if (!container) return

        const handleFooterWheelRelease = (event: WheelEvent) => {
            const swiper = swiperRef.current
            if (!swiper || !isSwiperMode()) return

            const deltaY = event.deltaY
            const deltaX = event.deltaX
            const scrollingDown = deltaY > 0

            if (!scrollingDown || !isLastSlide(swiper)) {
                resetFooterWheelIntent()
                return
            }

            if (isHorizontalNestedSwiperInteraction(event.target, deltaX, deltaY)) {
                return
            }

            const activeSlide = getActiveSlideElement()
            if (!activeSlide) return

            const slideScrollable = isElementScrollable(activeSlide)
            const activeSlideAtBottom = !slideScrollable || isScrollableElementAtBottom(activeSlide)

            if (!activeSlideAtBottom) {
                resetFooterWheelIntent()
                return
            }

            /**
             * Prevent Swiper from consuming this specific wheel event while we decide
             * whether it is a deliberate footer release intent.
             */
            event.preventDefault()
            event.stopPropagation()

            tryReleaseFooterFromWheel(deltaY, swiper, activeSlide)
        }

        container.addEventListener('wheel', handleFooterWheelRelease, {passive: false, capture: true})

        return () => {
            container.removeEventListener('wheel', handleFooterWheelRelease, {capture: true})
        }
    }, [])

    /**
     * managing scroll down button component scroll, 
     * changing slide instead moving outside animation component
     */
    useEffect(() => {
        const handleScrollDown = () => {
            swiperRef.current?.slideNext()
        }

        window.addEventListener('animated-scroll-down', handleScrollDown)

        return () => {
            window.removeEventListener('animated-scroll-down', handleScrollDown)
        }
    }, [])

    const renderSwiper = () => (
        <Swiper
            spaceBetween={useDesktopOverlapEffect ? 0 : calculateSpaceBetweenSlides()}
            direction="vertical"
            allowTouchMove={false}
            watchSlidesProgress={useDesktopOverlapEffect}
            effect={useDesktopOverlapEffect ? 'creative' : 'slide'}
            creativeEffect={useDesktopOverlapEffect ? DESKTOP_OVERLAP_EFFECT : undefined}
            pagination={{
                enabled: isDesktop,
                clickable: true,
            }}
            mousewheel={{
                enabled: true,
                releaseOnEdges: false,
                thresholdDelta: 25,
                thresholdTime: 300,
                forceToAxis: true,
            }}
            keyboard
            speed={600}
            modules={[Pagination, Mousewheel, Keyboard, EffectCreative]}
            className="mySwiper cc-animated-slide-swiper"
            onSwiper={(swiper) => {
                const currentSwiper = swiper as AnimatedSlideSwiper
                const originalSlidePrev = currentSwiper.slidePrev.bind(currentSwiper)

                currentSwiper.slidePrev = (...args) => {
                    activateReverseTransition()
                    return originalSlidePrev(...args)
                }

                swiper.pagination?.el?.addEventListener('click', handlePaginationClick)

                swiperRef.current = currentSwiper
            }}
            onRealIndexChange={(swiper) => {
                handleSlideChange(swiper)
            }}
            onSlideChangeTransitionEnd={() => {
                handleSlideChangeTranstionEnd()
            }}
        >
            {moduleItems?.map((component, idx) => (
                <SwiperSlide key={idx}>
                    <Box {...styles.moduleItemWrapper} data-testid="animated-slide-module-wrapper">
                        {component}
                    </Box>
                </SwiperSlide>
            ))}
        </Swiper>
    )

    return (
        <>
            <Box className="cc-animated-slide" data-testid="animated-slide" sx={styles.container} ref={containerRef}>
                {title_animation_type === 'carousel' && renderSwiper()}
            </Box>
        </>
    )
}

export {AnimatedSlide}
